import styled, { css, DefaultTheme, keyframes } from 'styled-components';
import {
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    FOCUS_OUTLINE_STYLE,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
    rem,
    Size,
    Sized,
    Variable,
} from '@optimus-web/common';
import { Icon } from '../Icon';
import {
    getActiveBackgroundColor,
    getActiveBorderColor,
    getActiveColor,
    getBackgroundColor,
    getBorderColor,
    getColor,
    getCounterActiveBackgroundColor,
    getCounterActiveColor,
    getCounterBackgroundColor,
    getCounterColor,
    getCounterDisabledBackgroundColor,
    getCounterDisabledColor,
    getCounterHoverBackgroundColor,
    getCounterHoverColor,
    getDisabledBackgroundColor,
    getDisabledBorderColor,
    getDisabledColor,
    getHoverBackgroundColor,
    getHoverBorderColor,
    getHoverColor,
    getLoaderIconColor,
} from './getButtonStyles';
import { ButtonVariant } from './types';

interface ButtonElementProps extends Required<Sized>, Variable<ButtonVariant> {
    wide?: boolean;
    $loading?: boolean;
}

export const getButtonHeight = ({ size, theme }: { size: Size; theme: DefaultTheme }) => {
    switch (size) {
        case 'small':
            return getToken('sizing400')({ theme });
        case 'medium':
            return getToken('sizing500')({ theme });
        case 'large':
            return getToken('sizing600')({ theme });
        case 'extraLarge':
            return getToken('sizing700')({ theme });
    }
};

export const getButtonPadding = ({ size, theme }: { size: Size; theme: DefaultTheme }) => {
    switch (size) {
        case 'small':
            return getSpacingToken('150')({ theme });
        case 'medium':
            return getSpacingToken('200')({ theme });
        default:
            return getSpacingToken('300')({ theme });
    }
};

const COLOR_TRANSITION = css`
    transition-property: background-color, border-color, color;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.fast}ms;
`;

export const CounterElement = styled.div<Variable<ButtonVariant>>`
    text-align: center;
    padding: 0 ${getSpacingToken('50')}px;
    height: ${({ theme }) => rem({ size: getToken('sizing200')({ theme }) })};
    box-sizing: border-box;
    min-width: ${getToken('sizing200')}px;
    border-radius: 100px;
    flex-shrink: ${getSpacingToken('0')};
    ${getTextTokenPreset('bodyExtraSmallStrong')};
    line-height: ${({ theme }) => rem({ size: getToken('sizing200')({ theme }) })};
    background-color: ${(props) => getCounterBackgroundColor(props.variant, props)};
    color: ${(props) => getCounterColor(props.variant, props)};
    border: ${({ variant, theme }) =>
        variant === 'inverse' &&
        `${getToken('borderWidth100')({ theme })}px solid ${getToken('borderStaticInverse')({ theme })}`};
    ${COLOR_TRANSITION};
`;

export const LoaderWrapper = styled.div`
    margin: 0;
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const ButtonElement = styled.button<ButtonElementProps>`
    background-color: ${(props) => getBackgroundColor(props.variant, props)};
    color: ${(props) => getColor(props.variant, props)};
    border: ${getToken('borderWidth100')}px solid ${(props) => getBorderColor(props.variant, props)};
    border-radius: ${getToken('borderRadius100')}px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    ${(props) => (props.wide ? 'width: 100%;' : null)};
    ${({ size, theme }) =>
        size === 'small'
            ? getTextTokenPreset('bodyMediumStrong')({ theme })
            : getTextTokenPreset('bodyLargeStrong')({ theme })}
    padding: 0 ${(props) => getButtonPadding(props)}px;
    height: ${(props) => getButtonHeight(props)}px;
    ${COLOR_TRANSITION};
    white-space: nowrap;
    cursor: pointer;
    position: relative;

    & > *:not(:last-child):not(${LoaderWrapper}) {
        margin-right: ${getSpacingToken('100')}px;
    }

    &:focus-visible {
        ${FOCUS_OUTLINE_STYLE}
        background-color: ${(props) =>
            props.variant === 'ghost' && getToken('backgroundInteractiveNeutralSubtleHover')};
    }

    &:disabled {
        cursor: not-allowed;
        pointer-events: none;

        ${({ $loading, ...props }) =>
            !$loading &&
            css`
                background-color: ${getDisabledBackgroundColor(props.variant, props)};
                color: ${getDisabledColor(props.variant, props)};
                border-color: ${getDisabledBorderColor(props.variant, props)};

                ${CounterElement} {
                    background-color: ${getCounterDisabledBackgroundColor(props.variant, props)};
                    color: ${getCounterDisabledColor(props.variant, props)};
                    border-color: ${props.variant === 'inverse' && getToken('borderStaticTertiary')(props)};
                }
            `}
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => getHoverBackgroundColor(props.variant, props)};
        color: ${(props) => getHoverColor(props.variant, props)};
        border-color: ${(props) => getHoverBorderColor(props.variant, props)};

        ${CounterElement} {
            background-color: ${(props) => getCounterHoverBackgroundColor(props.variant, props)};
            color: ${(props) => getCounterHoverColor(props.variant, props)};
        }
    }

    &:active:enabled {
        background-color: ${(props) => getActiveBackgroundColor(props.variant, props)};
        color: ${(props) => getActiveColor(props.variant, props)};
        border-color: ${(props) => getActiveBorderColor(props.variant, props)};

        ${CounterElement} {
            background-color: ${(props) => getCounterActiveBackgroundColor(props.variant, props)};
            color: ${(props) => getCounterActiveColor(props.variant, props)};
        }
    }

    ${(props) =>
        props.$loading &&
        css`
            color: transparent;

            > *:not(${LoaderWrapper}) {
                visibility: hidden;
            }
        `}
`;

const spin = keyframes`
    0% {
        transform: rotate(-90deg);
    }
    100% {
        transform: rotate(270deg);
    }
`;

export const ButtonLoaderIcon = styled(Icon)<{ buttonVariant: ButtonVariant }>`
    animation: ${spin} 1s linear infinite;
    transform-origin: 50% 50%;
    path {
        fill: ${(props) => getLoaderIconColor(props.buttonVariant, props)};
    }
`;
